import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useToggleTableColumns } from '@anytag/shared-hooks';
import { ColumnCustomiser, TableCell } from '@anytag/shared-atoms';
import { formatIntNumber, formatPercent, formatPrice } from '@anytag/shared-utils';
import { AllCampaignsReportSummary, CampaignReportOrderField } from '@anytag/graphql';
import { ReportColumnKeys, ReportRowData } from '../types';

interface Props {
  summaryData?: AllCampaignsReportSummary;
}

export const useReportTable = ({ summaryData }: Props) => {
  const { t } = useTranslation();
  // based on https://docs.google.com/spreadsheets/d/1lgx393NppgpNa90ACUgcNNLtlwXVNj_CqdhA-9-f7N4/edit#gid=301943529
  const { getSelectableColumns, handleToggleColumns } = useToggleTableColumns<ReportRowData, ReportColumnKeys>();

  const total = summaryData?.total ?? 0;
  const currency = t(summaryData?.currency || '');
  const columns: ColumnDef<ReportRowData>[] = [
    {
      enableHiding: false,
      accessorKey: 'campaign',
      meta: { sticky: true },
      header: ({ table }) => {
        const { selectableOptions } = getSelectableColumns(table);

        return (
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {t('HeaderColumn.Campaign Title')}
            <ColumnCustomiser
              options={selectableOptions}
              savedValuesKey="reportTableColumns"
              onChange={handleToggleColumns(table)}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { className, campaign } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal count={total} />
        ) : campaign ? (
          <TableCell.Campaign
            type={campaign.type}
            title={campaign.title}
            src={campaign.thumbNail}
            id={campaign.id.toString()}
            mkpCampaignType={campaign.mkpDetailType}
            isPostTracking={!!campaign.hasPostTracking}
          />
        ) : null;
      }
    },
    {
      enableHiding: false,
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Cost'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costHelp'),
        sortField: CampaignReportOrderField.COST
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.cost ? currency : ''}
          value={formatPrice(row.original.stats?.cost || null, currency)}
        />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'followers',
      header: t<string>('HeaderColumn.Follower'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.UniqueReachHelp'),
        sortField: CampaignReportOrderField.FOLLOWERS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.followers)} />
    },
    {
      accessorKey: 'impressions',
      header: t<string>('HeaderColumn.Impression'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressions)} />
    },
    {
      accessorKey: 'impressionRate',
      header: t<string>('HeaderColumn.Impression Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsRateHelp'),
        sortField: CampaignReportOrderField.IMPRESSION_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.impressionRate, false)} unit="%" />
    },
    {
      accessorKey: 'impressionsFromDiscovery',
      header: t<string>('HeaderColumn.Impression from Discovery'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromDiscoveryHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_DISCOVERY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromDiscovery)} />
    },
    {
      accessorKey: 'impressionsFromHashtag',
      header: t<string>('HeaderColumn.Impression from Hashtag'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromHashtagHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_HASHTAG
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromHashtag)} />
    },
    {
      accessorKey: 'impressionsFromHome',
      header: t<string>('HeaderColumn.Impression from Home'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromHomeHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_HOME
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromHome)} />
    },
    {
      accessorKey: 'impressionsFromProfile',
      header: t<string>('HeaderColumn.Impression from Profile'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromProfileHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_PROFILE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromProfile)} />
    },
    {
      accessorKey: 'impressionsFromOther',
      header: t<string>('HeaderColumn.Impression from Other'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.impressionsFromOtherHelp'),
        sortField: CampaignReportOrderField.IMPRESSIONS_FROM_OTHER
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.impressionsFromOther)} />
    },
    {
      enableHiding: false,
      accessorKey: 'view',
      header: t<string>('HeaderColumn.Video view'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.videoViewHelp'),
        sortField: CampaignReportOrderField.VIEW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.view)} />
    },
    {
      accessorKey: 'averageViewDurationMinutes',
      header: t<string>('HeaderColumn.Average View Duration (m)'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.averageViewDurationMinutesHelp'),
        sortField: CampaignReportOrderField.AVERAGE_VIEW_DURATION_MINUTES
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatIntNumber(row.original.stats?.averageViewDurationMinutes)} unit="m" />
      )
    },
    {
      accessorKey: 'averageViewDurationPercentage',
      header: t<string>('HeaderColumn.Average View Duration Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.averageViewDurationPercentageHelp'),
        sortField: CampaignReportOrderField.AVERAGE_VIEW_DURATION_PERCENTAGE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.averageViewDurationPercentage, false)} unit="%" />
      )
    },
    {
      accessorKey: 'reach',
      enableHiding: false,
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.reachHelp'),
        sortField: CampaignReportOrderField.REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.reach)} />
    },
    {
      accessorKey: 'reachRate',
      header: t<string>('HeaderColumn.Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.reachRateHelp'),
        sortField: CampaignReportOrderField.REACH_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.reachRate, false)} unit="%" />
    },
    {
      accessorKey: 'nonFollowerReach',
      header: t<string>('HeaderColumn.Non Follower Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nonFollowerReachHelp'),
        sortField: CampaignReportOrderField.NON_FOLLOWER_REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.nonFollowerReach)} />
    },
    {
      accessorKey: 'nonFollowerReachRate',
      header: t<string>('HeaderColumn.Non Follower Reach Rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nonFollowerReachRateHelp'),
        sortField: CampaignReportOrderField.NON_FOLLOWER_REACH_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.nonFollowerReachRate, false)} unit="%" />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.TotalEngagementsHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.engagement)} />
    },
    {
      enableHiding: false,
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.Egg Rate by followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementRateHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.engagementRate, false)} unit="%" />
    },
    {
      accessorKey: 'engagementViewsRate',
      header: t<string>('HeaderColumn.Egg Rate by views'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.engagementViewsRateHelp'),
        sortField: CampaignReportOrderField.ENGAGEMENT_VIEWS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats?.engagementViewsRate, false)} unit="%" />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'like',
      header: t<string>('HeaderColumn.Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeReactionHelp'),
        sortField: CampaignReportOrderField.LIKE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.like)} />
    },
    {
      accessorKey: 'dislikes',
      header: t<string>('HeaderColumn.Dislike'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.dislikesHelp'),
        sortField: CampaignReportOrderField.DISLIKES
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.dislikes)} />
    },
    {
      enableHiding: false,
      accessorKey: 'comment',
      header: t<string>('HeaderColumn.Comment'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.commentHelp'),
        sortField: CampaignReportOrderField.COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.comment)} />
    },
    {
      enableHiding: false,
      accessorKey: 'share',
      header: t<string>('HeaderColumn.Share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.shareHelp'),
        sortField: CampaignReportOrderField.SHARE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.share)} />
    },
    {
      accessorKey: 'retweets',
      header: t<string>('HeaderColumn.Retweets'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.retweetHelp'),
        sortField: CampaignReportOrderField.SHARE
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.share)} /> // Retweets
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.SaveHelp'),
        sortField: CampaignReportOrderField.SAVED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.saved)} />
    },
    {
      accessorKey: 'interaction',
      header: t<string>('HeaderColumn.Interaction'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.interactionHelp'),
        sortField: CampaignReportOrderField.INTERACTION
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.interaction)} />
    },
    {
      accessorKey: 'linkClicks',
      header: t<string>('HeaderColumn.Link Clicks'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.linkClicksHelp'),
        sortField: CampaignReportOrderField.LINK_CLICKS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.linkClicks)} />
    },
    {
      accessorKey: 'stickerTaps',
      header: t<string>('HeaderColumn.Sticker Taps'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.stickerTapsHelp'),
        sortField: CampaignReportOrderField.STICKER_TAPS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.stickerTaps)} />
    },
    {
      accessorKey: 'click',
      header: t<string>('HeaderColumn.Click'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.Click Action Help'),
        sortField: CampaignReportOrderField.CLICK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.click)} />
    },
    {
      enableHiding: false,
      accessorKey: 'conversion',
      header: t<string>('HeaderColumn.Conversion'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.Click Conversion Help'),
        sortField: CampaignReportOrderField.CONVERSION
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversion)} />
    },
    {
      accessorKey: 'order',
      header: t<string>('HeaderColumn.Orders'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ordersHelp'),
        sortField: CampaignReportOrderField.ORDER
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.orders)} />
    },
    {
      accessorKey: 'sales',
      enableHiding: false,
      header: t<string>('HeaderColumn.Sales'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.salesHelp'),
        sortField: CampaignReportOrderField.SALES
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales || null, currency)} unit={currency} />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'costsPerSale',
      header: t<string>('HeaderColumn.Commission rate'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerSaleHelp'),
        sortField: CampaignReportOrderField.COST_PER_SALE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerSale || null, currency)} unit={currency} />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'costsPerFollower',
      header: t<string>('HeaderColumn.Cost / Follower'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerFollowerHelp'),
        sortField: CampaignReportOrderField.COST_PER_FOLLOWER
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerFollower || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerImpressions',
      header: t<string>('HeaderColumn.Cost / Impression'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerImpressionsHelp'),
        sortField: CampaignReportOrderField.COST_PER_IMPRESSIONS
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.stats?.costsPerImpressions || null, currency)}
          unit={currency}
        />
      )
    },
    {
      accessorKey: 'costsPerView',
      header: t<string>('HeaderColumn.Cost / Video view'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerViewHelp'),
        sortField: CampaignReportOrderField.COST_PER_VIEW
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerView || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerReach',
      header: t<string>('HeaderColumn.Cost / Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerReachHelp'),
        sortField: CampaignReportOrderField.COST_PER_REACH
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerReach || null, currency)} unit={currency} />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'costsPerEngagement',
      header: t<string>('HeaderColumn.Cost / Engagement'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerEngagementHelp'),
        sortField: CampaignReportOrderField.COST_PER_ENGAGEMENT
      },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatPrice(row.original.stats?.costsPerEngagement || null, currency)}
          unit={currency}
        />
      )
    },
    {
      accessorKey: 'costsPerLike',
      header: t<string>('HeaderColumn.Cost / Like'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerLikeHelp'),
        sortField: CampaignReportOrderField.COST_PER_LIKE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerLike || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerComment',
      header: t<string>('HeaderColumn.Cost / Comment'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerCommentHelp'),
        sortField: CampaignReportOrderField.COST_PER_COMMENT
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerComment || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerShare',
      header: t<string>('HeaderColumn.Cost / Share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerShareHelp'),
        sortField: CampaignReportOrderField.COST_PER_SHARE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerShare || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerSaved',
      header: t<string>('HeaderColumn.Cost / Save'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerSavedHelp'),
        sortField: CampaignReportOrderField.COST_PER_SAVED
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerSaved || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerClick',
      header: t<string>('HeaderColumn.CPC'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerClickHelp'),
        sortField: CampaignReportOrderField.COST_PER_CLICK
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerClick || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerAction',
      header: t<string>('HeaderColumn.CPA'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerActionHelp'),
        sortField: CampaignReportOrderField.COST_PER_ACTION
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerAction || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'costsPerOrder',
      header: t<string>('HeaderColumn.CPO'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.costsPerOrderHelp'),
        sortField: CampaignReportOrderField.COST_PER_ORDER
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.costsPerOrder || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'back',
      header: t<string>('HeaderColumn.Back'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.backHelp'),
        sortField: CampaignReportOrderField.BACK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.back)} />
    },
    {
      accessorKey: 'exited',
      header: t<string>('HeaderColumn.Exited'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.exitedHelp'),
        sortField: CampaignReportOrderField.EXITED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.exited)} />
    },
    {
      accessorKey: 'forward',
      header: t<string>('HeaderColumn.Forward'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.forwardHelp'),
        sortField: CampaignReportOrderField.FORWARD
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.forward)} />
    },
    {
      accessorKey: 'nextStory',
      header: t<string>('HeaderColumn.Next Story'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.nextStoryHelp'),
        sortField: CampaignReportOrderField.NEXT_STORY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.nextStory)} />
    },
    {
      accessorKey: 'navigation',
      header: t<string>('HeaderColumn.Navigation'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.navigationHelp'),
        sortField: CampaignReportOrderField.NAVIGATION
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.navigation)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>('HeaderColumn.Profile Activity'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileActivityHelp'),
        sortField: CampaignReportOrderField.PROFILE_ACTIVITY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.profileActivity)} />
    }
  ];

  return { columns };
};
